import { Fragment, useState } from "react";
import { ChevronRight } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList, Cell } from "recharts";
import { fmt, rd, brandColor } from "./fniShared";

const COLS = [["leads", "Leads"], ["oportunidad", "Oport."], ["sometido", "Somet."], ["aprobado", "Aprob."],
              ["firmado", "Firm."], ["facturado", "Facturado"]];

function Row({ r, sub, open, onToggle, onSelect, selected, testid }) {
  return (
    <tr className={`border-b border-gray-50 ${sub ? "bg-gray-50/60" : "hover:bg-cyan-50/40 cursor-pointer"} ${selected ? "bg-cyan-50" : ""}`}
        onClick={sub ? undefined : onSelect} data-testid={testid}>
      <td className={`py-1.5 px-1.5 ${sub ? "pl-7 text-gray-600 font-semibold" : "font-extrabold text-gray-900"}`}>
        {!sub && (
          <button className="inline-flex align-middle mr-1 text-gray-400" onClick={(e) => { e.stopPropagation(); onToggle(); }}
                  aria-label="Ver marcas" data-testid={`${testid}-toggle`}>
            <ChevronRight size={14} className={`transition-transform ${open ? "rotate-90" : ""}`}/>
          </button>
        )}
        {sub && <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ background: brandColor(r.name) }}/>}
        {r.name}
      </td>
      {COLS.map(([k]) => (
        <td key={k} className={`py-1.5 px-1.5 text-right tabular-nums ${k === "facturado" ? "font-extrabold text-violet-700" : ""}`}>{fmt(r[k])}</td>
      ))}
      <td className="py-1.5 px-1.5 text-right tabular-nums whitespace-nowrap">{rd(r.monto_sometido)}</td>
    </tr>
  );
}

export default function FniSucursalTable({ sucursales, selected, onSelect }) {
  const [open, setOpen] = useState({});
  const chart = sucursales.flatMap((s) => [{ name: s.name, v: s.facturado, top: true },
    ...s.marcas.map((m) => ({ name: m.name, v: m.facturado, top: false }))]);
  return (
    <section className="surface p-4 mb-5 min-w-0" data-testid="fni-sucursales">
      <h3 className="font-display font-extrabold text-base">Por sucursal y marca</h3>
      <p className="text-[11px] text-gray-500 font-semibold mb-3">Haz clic en una sucursal para ver el embudo de cada marca · la flecha abre sus marcas</p>
      <div className="grid gap-5 xl:grid-cols-2">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-gray-400 border-b">
                <th className="py-2 px-1.5 text-left font-extrabold">Unidad de negocio</th>
                {COLS.map(([k, l]) => <th key={k} className="py-2 px-1.5 text-right font-extrabold">{l}</th>)}
                <th className="py-2 px-1.5 text-right font-extrabold">RD$ somet.</th>
              </tr>
            </thead>
            <tbody>
              {sucursales.map((s, i) => (
                <Fragment key={s.name}>
                  <Row r={s} open={open[s.name]} selected={selected === s.name} testid={`fni-suc-row-${i}`}
                       onToggle={() => setOpen((o) => ({ ...o, [s.name]: !o[s.name] }))} onSelect={() => onSelect(s.name)}/>
                  {open[s.name] && s.marcas.map((m, j) => <Row key={m.name} r={m} sub testid={`fni-suc-row-${i}-marca-${j}`}/>)}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ height: 280 }} data-testid="fni-facturado-chart">
          <div className="label-up text-[10px] mb-1">Facturado por sucursal y marca</div>
          <ResponsiveContainer width="100%" height="92%">
            <BarChart data={chart} margin={{ top: 18, right: 8, left: -18, bottom: 40 }}>
              <XAxis dataKey="name" interval={0} angle={-40} textAnchor="end" tick={{ fontSize: 10, fontWeight: 700 }}/>
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false}/>
              <Tooltip formatter={(v) => [fmt(v), "Facturado"]}/>
              <Bar dataKey="v" radius={[3, 3, 0, 0]}>
                {chart.map((c, i) => <Cell key={i} fill={c.top ? "#155e75" : brandColor(c.name)}/>)}
                <LabelList dataKey="v" position="top" style={{ fontSize: 10, fontWeight: 800 }}/>
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </section>
  );
}
